import { useState } from 'react'
import { ProductPicker, type PickerValue } from '@/components/ui/ProductPicker/ProductPicker'

export interface PricelistAwareProductPickerProps {
  customerId: number
  value: PickerValue
  onChange: (next: PickerValue) => void
  editorMode?: boolean
  onCancel?: () => void
}

/**
 * Forecast-entry product picker — defaults to the customer's existing
 * Customer Pricelist ("what do they already buy") rather than the full
 * catalog, per stakeholder feedback on the shipped V1 grid. A rep can
 * always switch to full-catalog search (e.g. the product isn't on the
 * pricelist yet); picking an off-pricelist product only sets it for THIS
 * forecast line — it never writes to the customer's pricelist. Pricelist
 * maintenance stays exclusively in the Customer Pricelists module.
 *
 * No separate "does this customer have a pricelist" check is needed: an
 * empty pricelist-mode result is just zero matches, and the toggle below
 * is always available regardless of result count.
 */
export function PricelistAwareProductPicker({
  customerId,
  value,
  onChange,
  editorMode,
  onCancel,
}: PricelistAwareProductPickerProps) {
  const [mode, setMode] = useState<'pricelist' | 'catalog'>('pricelist')

  return (
    <div className="flex flex-col gap-1">
      <ProductPicker
        value={value}
        onChange={onChange}
        editorMode={editorMode}
        onCancel={onCancel}
        searchEndpoint={
          mode === 'pricelist'
            ? `/api/forecasts/customers/${customerId}/pricelist-products`
            : undefined
        }
        placeholder={mode === 'pricelist' ? 'Search their pricelist…' : 'Search full catalog…'}
      />
      <button
        type="button"
        onClick={() => setMode((m) => (m === 'pricelist' ? 'catalog' : 'pricelist'))}
        className="cursor-pointer self-start text-2xs text-link hover:underline"
      >
        {mode === 'pricelist' ? 'Not on their pricelist? Search full catalog' : 'Show their pricelist only'}
      </button>
    </div>
  )
}
